import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { DEVTOOLS_OPTIONS } from '@/modules/debug/constants/zustand';
import { LoadCarrier } from '@/modules/api/PimApiConnector/mapper';
import { logger } from '@common/logging/logger';

type State = {
  allLoadCarriersState: LoadCarrier[]; // get from pimCore
};

const INITIAL_STATE: State = {
  allLoadCarriersState: [],
};

type Action = {
  allLoadCarriers: {
    set(vehicles: LoadCarrier[]): void;
    getAll(): LoadCarrier[];
    getByIds(ids: string[]): LoadCarrier[];
    getById(id: string): LoadCarrier | undefined;
    getByIdSilently(id: string): LoadCarrier | undefined;
    clear(): void;
  };
  reset(): void;
};

export const useLoadCarrierStore = create<State & Action>()(
  devtools(
    (set, get) => ({
      ...INITIAL_STATE,

      allLoadCarriers: {
        set(loadCarriers) {
          set(
            {
              allLoadCarriersState: loadCarriers,
            },
            undefined,
            {
              type: 'allLoadCarriers/set',
            },
          );
        },

        getAll() {
          return get().allLoadCarriersState;
        },

        getByIds(ids) {
          return get().allLoadCarriersState.filter((v) => ids.includes(v.id));
        },

        getById(id) {
          const loadCarrier = get().allLoadCarriers.getByIdSilently(id);

          if (!loadCarrier) {
            logger.error(`Could not find load carrier with id: ${id}`);
            return;
          }

          return loadCarrier;
        },

        getByIdSilently(id) {
          return get().allLoadCarriersState.find((v) => v.id === id);
        },

        clear() {
          set(
            {
              allLoadCarriersState: [],
            },
            undefined,
            {
              type: 'allLoadCarriers/clear',
            },
          );
        },
      },

      reset() {
        set(INITIAL_STATE, undefined, {
          type: 'reset',
        });
      },
    }),
    { store: 'loadCarrierStore', ...DEVTOOLS_OPTIONS },
  ),
);
